import * as Sentry from "@sentry/react";
import mixpanel from "mixpanel-browser";
import { IS_PRODUCTION } from "../getServiceUrl";

const MIXPANEL_TOKEN = IS_PRODUCTION ? "6f8f776009e6493f83a96e9745b247cd" : "47ce4fe975ccfb3062579e9322d24f99";

/** 마지막으로 identify 한 사용자 id. 계정 전환 감지에만 쓴다. */
const LAST_IDENTIFIED_KEY = "bricks:last-identified-user";

/**
 * 프로젝트(현장) 단위 분석의 group key.
 *
 * 건설 현장 앱에서 리텐션의 실제 단위는 사람이 아니라 **현장**이다. 한 현장이 계속
 * 일일 로그를 쓰고 있는지가 이탈 예측의 핵심인데, 사용자 단위 집계로는 "현장 3곳 중
 * 1곳만 살아 있는 계정"과 "3곳 모두 활발한 계정"이 구분되지 않는다.
 *
 * 주의: 이 키는 Mixpanel 프로젝트 설정(Group Keys)에도 동일한 이름으로 등록돼 있어야
 * group 분석이 동작한다. 등록 전에는 이벤트에 실리기만 하고 집계에는 잡히지 않는다.
 */
export const PROJECT_GROUP_KEY = "project";

/**
 * 현재 등록된 현장 group.
 *
 * 관측 시점의 현장을 떠 두었다가 나중에 전송하는 이벤트(이탈, 지연된 API 실패)에 쓴다.
 */
export function getProjectGroup(): string[] | null {
  try {
    return (mixpanel.get_property(PROJECT_GROUP_KEY) as string[] | undefined) ?? null;
  } catch {
    return null;
  }
}

/**
 * PWA 창(홈 화면에서 실행)인지 브라우저 탭인지 구분한다.
 * iOS Safari 는 display-mode 미디어쿼리 대신 비표준 `navigator.standalone` 을 쓴다.
 */
function isStandaloneDisplayMode(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    ("standalone" in navigator && (navigator as { standalone?: boolean }).standalone === true)
  );
}

/**
 * 모든 이벤트에 자동으로 붙는 super properties.
 *
 * - `app_version`: 배포별 비교 및 구버전 잔존율 측정의 기준값 (Sentry release 와 동일한 값)
 * - `is_standalone`: 설치된 PWA 사용자와 브라우저 사용자를 분리해서 볼 수 있게 한다
 *
 * `mixpanel.reset()` 은 super properties 도 함께 지우므로 reset 직후에는 반드시 다시 호출해야 한다.
 */
function registerSuperProperties(): void {
  mixpanel.register({
    app_version: __BUILD_HASH__,
    is_standalone: isStandaloneDisplayMode(),
  });
}

/**
 * 식별된 사용자에 대해 분석 축으로 쓰는 속성.
 *
 * 서버(`/api/user`)가 이미 내려주고 있는데 지금까지 보내지 않던 값들이다.
 * 이게 없으면 "직군별 작성 완료율", "가입 경로별 리텐션" 같은 기본 세그먼트 질의가
 * 아예 성립하지 않는다.
 */
export type IdentifiedUser = {
  name: string;
  email: string;
  /** 서버 권한 목록. 첫 번째 값을 대표 직군으로 본다. */
  roles?: string[];
  /** 가입 경로 */
  source?: string;
  isEmailVerified?: boolean;
  /** 계약 만료일. 만료가 가까운 계정의 사용 패턴을 보기 위한 축. */
  serviceDueDate?: string | null;
};

/**
 * 사용자 속성을 super property 로도 등록한다.
 *
 * Mixpanel 에서 people property 는 프로필 조회와 코호트에만 쓰이고 **이벤트 breakdown
 * 에는 걸리지 않는다.** 세그먼트 축으로 쓰려면 같은 값이 이벤트에도 실려야 하므로
 * `people.set` 과 `register` 두 곳에 나눠 심는다.
 *
 * `user_role` 은 대표 직군 하나(스칼라)라 breakdown 이 안정적이고,
 * `user_roles` 는 목록이라 "이 권한을 가진 사람" 필터에 쓴다.
 */
function registerUserProperties(user: IdentifiedUser): void {
  mixpanel.register({
    user_role: user.roles?.[0] ?? "unknown",
    user_roles: user.roles ?? [],
    role_count: user.roles?.length ?? 0,
    user_source: user.source ?? null,
  });
}

function readLastIdentifiedUserId(): string | null {
  try {
    return localStorage.getItem(LAST_IDENTIFIED_KEY);
  } catch {
    return null;
  }
}

function writeLastIdentifiedUserId(userId: string): void {
  try {
    localStorage.setItem(LAST_IDENTIFIED_KEY, userId);
  } catch {
    // 프라이빗 모드 등 저장 실패 시 계정 전환 감지만 포기한다
  }
}

// biome-ignore lint/complexity/noStaticOnlyClass: abstract base class for analytics loggers — not meant to be instantiated
export abstract class BaseLogger {
  public static init(): void {
    try {
      mixpanel.init(MIXPANEL_TOKEN, {
        debug: import.meta.env.DEV,
        // Safari ITP 는 스크립트로 심은 쿠키를 7일 뒤 만료시킨다. 쿠키 persistence 를 쓰면
        // 재방문 유저가 신규로 잡혀 리텐션이 과소·신규 유저가 과대 집계된다.
        persistence: "localStorage",
        // 자동 페이지뷰를 끈다. SDK 는 첫 페이지뷰를 init() **안에서** 동기로 쏘고
        // (Autocapture.initPageviewTracking), SPA 이동에서는 history 변경 리스너로 쏜다.
        // 둘 다 우리가 group 을 정리·갱신하기 전 시점이라, 직전 현장이 붙은 페이지뷰를
        // 막을 방법이 없다. 게다가 이 이벤트는 식별자가 든 raw URL 을 싣고 있어
        // 화면 단위 집계에도 쓸 수 없다.
        //
        // 대신 화면마다 명시적인 `*_view` 이벤트를 발행한다. 정규화된 `route_pattern` 과
        // 올바른 현장이 붙고, 화면별로 필요한 프로퍼티까지 함께 실린다.
        // 주의: 이 설정을 되돌리면 `$mp_web_page_view` 의 현장 귀속이 다시 깨진다.
        track_pageview: false,
      });

      // group 은 super property 라 localStorage 에 영속된다. 프로젝트 화면에서 앱이
      // 강제 종료되면(모바일 PWA 의 정상 종료 경로다) 해제 코드가 돌 기회가 없어,
      // 다음 실행의 홈·로그인 이벤트까지 직전 현장에 붙는다. 첫 이벤트 전에 끊는다.
      mixpanel.unregister(PROJECT_GROUP_KEY);
      registerSuperProperties();
    } catch (error) {
      Sentry.captureException(new Error("Failed to initialize mixpanel"), {
        extra: {
          error,
        },
      });
    }
  }

  public static identify(userId: string, user?: IdentifiedUser): void {
    try {
      // 공용 태블릿을 여러 작업자가 돌려쓰는 현장이 있다. 앱에 로그아웃 경로가 없어
      // reset() 을 걸 지점이 없으므로, 직전 사용자와 "다른" 사용자가 들어오면 여기서 identity 를 끊는다.
      // 그렇지 않으면 distinct_id 가 유지되어 서로 다른 작업자의 행동이 한 유저로 합쳐진다.
      // 최초 로그인(직전 사용자 없음)에는 reset 하지 않는다 — 로그인 전 익명 이벤트와의 연결이 끊긴다.
      const lastUserId = readLastIdentifiedUserId();
      if (lastUserId !== null && lastUserId !== userId) {
        BaseLogger.reset();
      }

      mixpanel.identify(userId);

      // identify 가 끝난 뒤에 기록한다. 먼저 쓰면 identify 가 실패했을 때
      // "이미 전환됐다"고 오인해 다음 기회에도 reset 하지 않는다.
      if (lastUserId !== userId) {
        writeLastIdentifiedUserId(userId);
      }

      if (user) {
        mixpanel.people.set({
          $name: user.name,
          $email: user.email,
          roles: user.roles ?? [],
          source: user.source ?? null,
          is_email_verified: user.isEmailVerified ?? null,
          service_due_date: user.serviceDueDate ?? null,
        });

        // reset() 이 super properties 를 비우므로 identify 뒤에 등록해야 한다.
        registerUserProperties(user);
      }
    } catch (error) {
      Sentry.captureException(new Error("Failed to identify user"), {
        extra: {
          error,
        },
      });
    }
  }

  public static reset(): void {
    try {
      mixpanel.reset();
      // reset() 이 super properties 까지 비우므로 즉시 복구한다.
      registerSuperProperties();
    } catch (error) {
      Sentry.captureException(new Error("Failed to reset mixpanel"), {
        extra: {
          error,
        },
      });
    }
  }

  /**
   * 현재 보고 있는 프로젝트를 group 으로 지정한다.
   *
   * 이후 발생하는 모든 이벤트가 이 현장에 귀속되므로, 현장 단위 활성도·리텐션을
   * 사람 단위 집계와 따로 볼 수 있다. 프로젝트 화면을 벗어나면 반드시
   * {@link clearProjectGroup} 으로 풀어야 한다 — group 은 super property 라
   * localStorage 에 영속돼서, 남겨두면 홈·로그인 화면 이벤트까지 직전 현장에 붙는다.
   * (강제 종료로 해제를 못 한 경우는 {@link init} 이 받아낸다.)
   *
   * `mixpanel.set_group()` 을 쓰지 않는다. 그 API 는 super property 와 함께
   * **유저 프로필에도** 같은 값을 쓰는데(`people.set`), 해제 시 지워지지 않아
   * 프로필의 `project` 가 "마지막으로 본 현장"에 영구히 고착된다. 여기서 필요한 건
   * "이 이벤트가 일어난 현장"이지 "이 사람이 속한 현장"이 아니다 —
   * 후자는 {@link setProjectMembership} 이 맡는다.
   */
  public static setProjectGroup(projectId: string, name?: string): void {
    try {
      // group 분석은 이벤트에 실린 group key 로 동작한다. 값 형태는 set_group 과 동일하게 배열.
      mixpanel.register({ [PROJECT_GROUP_KEY]: [projectId] });

      // 그룹 프로필은 이름을 알게 된 화면에서만 갱신한다.
      // 모르는 채로 덮어쓰면 Mixpanel 에서 현장이 id 로만 보여 읽을 수 없게 된다.
      if (name) {
        mixpanel.get_group(PROJECT_GROUP_KEY, projectId).set({ $name: name });
      }
    } catch (error) {
      Sentry.captureException(new Error("Failed to set project group"), { extra: { error, projectId } });
    }
  }

  /**
   * 사용자가 속한 현장 수를 분석 축으로 남긴다.
   *
   * {@link setProjectGroup} 과 혼동하기 쉽지만 답하는 질문이 다르다.
   * group 은 "이 **이벤트**가 어느 현장에서 일어났나"라서 항상 한 곳이고,
   * 이 값은 "이 **사람**이 몇 곳을 맡고 있나"라서 여러 곳이다.
   * 현장 1곳뿐인 작업자와 5곳을 도는 관리자는 사용 패턴이 전혀 달라 반드시 갈라 봐야 한다.
   *
   * people property 는 이벤트 breakdown 에 걸리지 않으므로 super property 로도 등록한다.
   * 한 번 심으면 프로필과 localStorage 에 남으므로, 목록을 아는 화면(홈)에서만 갱신해도
   * 이후 다른 화면의 이벤트까지 이 축으로 나눌 수 있다.
   */
  public static setProjectMembership(projectCount: number): void {
    try {
      mixpanel.register({ project_count: projectCount });
      mixpanel.people.set({ project_count: projectCount });
    } catch (error) {
      Sentry.captureException(new Error("Failed to set project membership"), { extra: { error, projectCount } });
    }
  }

  public static clearProjectGroup(): void {
    try {
      mixpanel.unregister(PROJECT_GROUP_KEY);
    } catch (error) {
      Sentry.captureException(new Error("Failed to clear project group"), { extra: { error } });
    }
  }
}
